import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantViewings } from '../../hooks/useViewings';
import { formatTime, formatDate } from '../../services/viewings.service';
import { getApplicationsForTenant, createPropertyApplication } from '../../utils/propertyApplications';
import { FirebaseStorageUploadService } from '../../services/firebaseStorageUpload';
import { markNotificationsByBucketAsRead } from '../../utils/notifications';
import {
  Calendar,
  Clock,
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Upload,
  FileText,
  ExternalLink
} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_BADGES = {
  pending:   { icon: AlertCircle, color: 'bg-yellow-50 text-yellow-800 border-yellow-200', label: 'In afwachting', iconColor: 'text-yellow-500' },
  accepted:  { icon: CheckCircle, color: 'bg-green-50 text-green-800 border-green-200', label: 'Bevestigd', iconColor: 'text-green-500' },
  confirmed: { icon: CheckCircle, color: 'bg-green-50 text-green-800 border-green-200', label: 'Bevestigd', iconColor: 'text-green-500' },
  declined:  { icon: XCircle, color: 'bg-red-50 text-red-800 border-red-200', label: 'Afgewezen', iconColor: 'text-red-500' },
  cancelled: { icon: XCircle, color: 'bg-gray-50 text-gray-800 border-gray-200', label: 'Geannuleerd', iconColor: 'text-gray-500' },
  completed: { icon: CheckCircle, color: 'bg-blue-50 text-blue-800 border-blue-200', label: 'Voltooid', iconColor: 'text-blue-500' },
  no_show:   { icon: AlertCircle, color: 'bg-orange-50 text-orange-800 border-orange-200', label: 'Niet verschenen', iconColor: 'text-orange-500' }
};

export default function TenantViewings() {
  const { user } = useAuth();
  const { viewings, loading } = useTenantViewings();
  const [applicationsByViewingId, setApplicationsByViewingId] = useState({});
  const [uploadingViewingId, setUploadingViewingId] = useState(null);
  const motivationFileRef = useRef(null);

  useEffect(() => {
    if (user?.uid) {
      loadApplications();
      markNotificationsByBucketAsRead(user.uid, 'viewings');
    }
  }, [user?.uid]);

  const loadApplications = async () => {
    try {
      const apps = await getApplicationsForTenant(user.uid);
      const map = {};
      apps.forEach(app => {
        if (app.viewingId) map[app.viewingId] = app;
      });
      setApplicationsByViewingId(map);
    } catch (error) {
      console.error('Error loading motivation applications:', error);
    }
  };

  const handleUploadClick = (viewingId) => {
    if (!motivationFileRef.current) return;
    motivationFileRef.current.dataset.viewingId = viewingId;
    motivationFileRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    const viewingId = event.target.dataset.viewingId;
    event.target.value = '';

    if (!file || !viewingId || !user?.uid) return;

    if (applicationsByViewingId[viewingId]) {
      toast.error('Je hebt al een motivatiebrief ingediend voor deze bezichtiging');
      return;
    }

    const viewing = viewings.find(v => v.id === viewingId);
    if (!viewing) {
      toast.error('Bezichtiging niet gevonden');
      return;
    }

    try {
      setUploadingViewingId(viewingId);
      const propertyId = viewing.listingId || viewing.propertyId || viewing.kotID;
      if (!propertyId) {
        throw new Error('Geen pand gekoppeld aan deze bezichtiging');
      }

      const folder = `motivations/${propertyId}`;
      const fileName = `motivation_${user.uid}_${viewingId}_${Date.now()}.pdf`;
      const uploadResult = await FirebaseStorageUploadService.uploadDocument(file, folder, fileName);

      await createPropertyApplication({
        propertyId,
        viewingId,
        tenantId: user.uid,
        motivationLetterUrl: uploadResult.url,
        motivationLetterPath: uploadResult.path,
        tenantSnapshot: {
          displayName: user.displayName || '',
          email: user.email || '',
          uid: user.uid
        }
      });

      toast.success('Motivatiebrief succesvol ingediend!');
      await loadApplications();
    } catch (error) {
      console.error('Error uploading motivation letter:', error);
      toast.error(error.message || 'Upload mislukt');
    } finally {
      setUploadingViewingId(null);
    }
  };

  const getStatus = (status) => STATUS_BADGES[status] || STATUS_BADGES.pending;

  // Check if a viewing is today
  const isToday = (date) => {
    const today = new Date();
    return date.getFullYear() === today.getFullYear() &&
           date.getMonth() === today.getMonth() &&
           date.getDate() === today.getDate();
  };

  // Check if motivation letter can be submitted (any non-disallowed viewing, no letter yet)
  const needsMotivation = (viewing) => {
    const disallowed = ['declined', 'cancelled', 'no_show'];
    return !disallowed.includes(viewing.status) &&
           !applicationsByViewingId[viewing.id];
  };

  // Check if viewing is today or past and still needs motivation (for urgency badge)
  const needsMotivationToday = (viewing) => {
    const startAt = viewing.startAt?.toDate ? viewing.startAt.toDate() : new Date(viewing.startAt);
    const today = new Date();
    const isPastOrToday = startAt <= new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    return isPastOrToday && needsMotivation(viewing);
  };

  return (
    <div className="container-professional py-8">
      {/* Hidden file input */}
      <input
        ref={motivationFileRef}
        type="file"
        accept=".pdf"
        className="hidden"
        onChange={handleFileChange}
      />

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Bezichtigingen</h1>
        <p className="text-gray-600">Bekijk al je bezichtigingsaanvragen en dien motivatiebrieven in</p>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#5EABEB] mx-auto"></div>
            <p className="mt-4 text-gray-600">Bezichtigingen laden...</p>
          </div>
        </div>
      ) : viewings.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12">
          <div className="text-center">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Eye className="w-10 h-10 text-green-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Geen bezichtigingen</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Je hebt nog geen bezichtigingen aangevraagd. Vraag een bezichtiging aan voor koten die je interessant vindt.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {viewings.map((viewing) => {
            const startAt = viewing.startAt?.toDate ? viewing.startAt.toDate() : new Date(viewing.startAt);
            const endAt = viewing.endAt?.toDate ? viewing.endAt.toDate() : new Date(viewing.endAt);
            const statusInfo = getStatus(viewing.status);
            const StatusIcon = statusInfo.icon;
            const application = applicationsByViewingId[viewing.id];
            const disallowed = ['declined', 'cancelled', 'no_show'];
            const canUpload = !disallowed.includes(viewing.status) && !application;
            const todayAlert = needsMotivationToday(viewing);

            return (
              <div
                key={viewing.id}
                className={`bg-white rounded-2xl shadow-sm border p-6 hover:shadow-md transition-shadow ${
                  todayAlert ? 'border-red-300 ring-1 ring-red-200' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${statusInfo.color.split(' ')[0]}`}>
                      <StatusIcon className={`w-5 h-5 ${statusInfo.iconColor}`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {viewing.propertyTitle || `Bezichtiging #${viewing.id.slice(-6)}`}
                      </h3>
                      {viewing.landlordName && (
                        <p className="text-sm text-gray-500">{viewing.landlordName}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {todayAlert && (
                      <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200 animate-pulse">
                        <span className="w-2 h-2 rounded-full bg-red-500"></span>
                        Motivatiebrief vereist
                      </span>
                    )}
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center text-gray-700">
                    <Calendar className="w-4 h-4 mr-2 text-[#5EABEB]" />
                    <span className="text-sm font-medium">
                      {isToday(startAt) ? (
                        <span className="text-red-600 font-bold">Vandaag</span>
                      ) : (
                        formatDate(startAt)
                      )}
                    </span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <Clock className="w-4 h-4 mr-2 text-[#5EABEB]" />
                    <span className="text-sm font-medium">{formatTime(startAt)} - {formatTime(endAt)}</span>
                  </div>
                  {viewing.location && (
                    <div className="flex items-center text-gray-700">
                      <MapPin className="w-4 h-4 mr-2 text-[#5EABEB]" />
                      <span className="text-sm font-medium truncate">{viewing.location}</span>
                    </div>
                  )}
                </div>

                {viewing.notes && (
                  <p className="mt-3 text-sm text-gray-600">
                    <span className="font-medium text-gray-900">Opmerkingen:</span> {viewing.notes}
                  </p>
                )}

                {/* Motivation letter section */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  {application ? (
                    <div className="flex items-center justify-between bg-green-50 rounded-xl p-4 border border-green-200">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-green-900">Motivatiebrief ingediend</p>
                          <p className="text-xs text-green-700">
                            Status: {application.status?.replace('_', ' ') || 'ingediend'}
                          </p>
                        </div>
                      </div>
                      {application.motivationLetterUrl && (
                        <button
                          onClick={() => window.open(application.motivationLetterUrl, '_blank')}
                          className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Bekijken
                        </button>
                      )}
                    </div>
                  ) : canUpload ? (
                    <div className={`flex items-center justify-between rounded-xl p-4 border ${
                      todayAlert
                        ? 'bg-red-50 border-red-200'
                        : 'bg-slate-50 border-slate-200'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                          todayAlert ? 'bg-red-100' : 'bg-slate-100'
                        }`}>
                          <FileText className={`w-5 h-5 ${todayAlert ? 'text-red-600' : 'text-slate-500'}`} />
                        </div>
                        <div>
                          <p className={`text-sm font-semibold ${todayAlert ? 'text-red-900' : 'text-slate-900'}`}>
                            {todayAlert ? 'Dien je motivatiebrief in!' : 'Motivatiebrief indienen'}
                          </p>
                          <p className={`text-xs ${todayAlert ? 'text-red-700' : 'text-slate-600'}`}>
                            Upload een PDF met je motivatie
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleUploadClick(viewing.id)}
                        disabled={uploadingViewingId === viewing.id}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-50 ${
                          todayAlert
                            ? 'bg-red-600 hover:bg-red-700'
                            : 'bg-[#5EABEB] hover:bg-[#4A98D6]'
                        }`}
                      >
                        <Upload className="w-4 h-4" />
                        {uploadingViewingId === viewing.id ? 'Uploaden...' : 'Upload PDF'}
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      Motivatiebrief kan niet ingediend worden voor deze status.
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
